<template>
  <div id="app-ves-clickstream" ref="appWrapperRef"> <!-- Hauptdiv Beginn -->
    <!-- Anzeige der Progressbar nur für States in QUESTION_STEPS_ORDER -->
    <div v-if="workflowStore.shouldShowHeadline" class="headlist content">
      <ProgressBar
          :currentStep="workflowStore.currentStep"
          :totalSteps="workflowStore.totalSteps"
          aria-label="Fortschritt im Fragebogen"
      />
    </div>
    <!-- Hauptinhaltsbereich Beginn -->
    <div id="main_content" ref="mainContentRef" tabindex="-1">
      <Transition name="fade-slide" mode="out-in">
        <component
            :is="currentComponent"
            :key="workflowStore.currentMachineState"
            class="content"
        />
      </Transition>
    </div>
    <!-- Hauptinhaltsbereich Ende -->
    <!-- Zurückbutton für alle States außer die Erfolgsseite und die erste Frage -->
    <button :class="['button button_ghost', { 'is-hidden': !workflowStore.shouldShowBackButton }]"
            @click="workflowStore.sendWorkflowEvent({ type: 'BACK' })"
            :aria-hidden="!workflowStore.shouldShowBackButton"
            :tabindex="workflowStore.shouldShowBackButton ? 0 : -1">
      Zurück
    </button>
  </div> <!-- Hauptdiv Ende -->
</template>

<style>

:host {
  all: initial;
  font-size: 16px;
  display: block;
  width: 100%;
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  line-height: normal;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: transparent;
}

/* Dein innerer Reset */
:host *, :host *::before, :host *::after {
  box-sizing: border-box;
}

/* --- Scope: #app-ves-clickstream --- */
#app-ves-clickstream {
  /* Variables */
  --primary-background-color: #edf1f6;
  --secondary-color: #5cb85c;
  --input-background-color: white;
  --color-yellow: #ffda00;
  --color-yellow-hover: #ffe966;
  --primary-text-color: black;
  --secondary-text-color: #333;
  --tertiary-text-color: white;
  --error-text-color: #b1150d;
  --input-border-color: #f2f2f2;
  --focus-border-color: #606060;
  --outline-color: #2071b5;
  --ghost-button-color: transparent;
  --ghost-button-hover-color: #222;
  --ghost-button-active-color: #4D4D4D;

  /* --- LAYOUT CARDS SYSTEM (Vorgabe Kunde) --- */

  /* 1. Radien (Radius) */
  --form-layoutcard-radius-default: 3px;
  --form-layoutcard-radius-focus: 5px;

  /* 2. REINE FARBEN (Für Border und Box-Shadow) */
  --form-layoutcard-color-default: #CCCCCC;
  --form-layoutcard-color-hover: #808080;
  --form-layoutcard-color-press: #808080;
  --form-layoutcard-color-selected: #333333;
  --form-layoutcard-color-focus: #2071B5;

  /* 3. Hintergründe (Background) */
  --form-layoutcard-bg-default: #FFFFFF;
  --form-layoutcard-bg-press: #F8F8F8;

  /* 4. Fertige Rahmen-Definitionen (Borders) */
  --form-layoutcard-border-default: 1px solid var(--form-layoutcard-color-default);
  --form-layoutcard-border-focus: 2px solid var(--form-layoutcard-color-focus);
  --form-layoutcard-border-transparent: 1px solid transparent;

  /* 5. Fokus-Sonderregeln */
  --form-layoutcard-focus-offset: 4px;

  /* 6. Transitions */
  /*
  --form-layoutcard-hover-transition: background-color 150ms ease-out 0ms, box-shadow 150ms ease-out 0ms;
  --form-layoutcard-active-transition: background-color 50ms ease-out 0ms, box-shadow 50ms ease-out 0ms;
   */
  --form-layoutcard-hover-transition: all 150ms ease-out 0ms;
  --form-layoutcard-active-transition: all 50ms ease-out 0ms;

  --color-grey-30: #B2B2B2;

  /* Base Styles (früher body-Selektor) */
  color: var(--primary-text-color);
  font-family: "VattenfallHall-Regular", Arial, sans-serif;
  font-size: 16px;
  font-weight: 300;
  /* ALT:
  margin: 0;
  padding: 20px;
  padding-top: 20px;
   */
  margin: 0;
  padding: 32px 16px 40px 16px;
  background: var(--primary-background-color);

  @media screen and (min-width: 800px) {
    padding-top: 65px;
  }

  position: relative;

  & img {
    max-width: 100%;
  }

  & h3 {
    text-align: center;
    font-family: "VattenfallHall-Bold", "VattenfallHall-Regular", Arial, Helvetica, sans-serif;
    font-size: 20px;
    font-weight: 400;
    margin-top: 0;

  }

  & .formdata h3 {
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.5;
  }

  & h4 {
    font-family: "VattenfallHall-Bold", "VattenfallHall-Regular", Arial, Helvetica, sans-serif;
  }

  & strong {
    font-family: "VattenfallHall-Bold", "VattenfallHall-Regular", Arial, Helvetica, sans-serif;
    font-size: 1.2em;
  }

  & fieldset {
    border: none;
  }

  & .header-container {
    min-height: 86px;
    margin-bottom: 30px;
    @media (max-width: 767px) {
      min-height: 86px;
    }
  }

  /* Layout */
  & .grid {
    display: grid;
    grid-gap: 20px;

    @media screen and (max-width: 800px) {
      grid-template-rows: 1fr;
    }

    @media screen and (min-width: 800px) {
      grid-template-columns: 1fr 1fr;
    }
  }

  & .content {
    font-family: "VattenfallHall-Regular", Arial, Helvetica, sans-serif;
    box-sizing: border-box;
    max-width: 1300px;
    margin-right: auto;
    margin-left: auto;
    font-size: 14px;
  }

  & .left { float: left; }
  & .right { float: right; }
  & .bottom.centered { width: 100%; }
  & .centered img { margin-top: 20px; }

  /* Form Elements */
  & input {
    font-size: 14px;
    padding: .7rem;
    border-width: .2rem;
    border-style: solid;
    border-radius: 2rem;
    background-color: var(--input-background-color);
    border-color: var(--input-border-color);
    margin-right: 20px;

    &:focus,
    &:focus-visible {
      outline: 1px solid var(--focus-border-color);
      border-color: var(--focus-border-color);
    }

    @media (max-width: 768px) {
      & {
        font-size: 16px;
        -webkit-tap-highlight-color: transparent; /* Entfernt den iOS-Auswahl-Blitz */
      }
    }
  }

  & .formdata {
    display: block;
  }

  /* Buttons */
  & .button {
    font-family: "VattenfallHall-Regular", Arial, Helvetica, sans-serif;
    font-size: 14px;
    text-align: center;
    cursor: pointer;
    border: 1px solid transparent;
    border-radius: 2rem;

    @supports selector(:focus-visible) {
      &:focus-visible {
        outline: 2px solid var(--outline-color);
        outline-offset: .4rem;
      }
    }

    @supports not selector(:focus-visible) {
      &:focus {
        outline: 2px solid var(--outline-color);
        outline-offset: .4rem;
      }
    }

    @media screen and (max-width: 800px) {
      margin-bottom: 10px;
      padding: 0.7rem 2rem;
    }

    @media screen and (min-width: 800px) {
      margin-right: 15px;
      padding: 0.7rem 4rem;
    }
  }

  & a.button {
    appearance: button;
    text-decoration: none;
    color: initial;
  }

  & .button_yellow {
    color: var(--secondary-text-color);
    background: var(--color-yellow);

    &:hover {
      background: var(--color-yellow-hover);
    }
  }

  & .button_ghost {
    background-color: var(--ghost-button-color);
    color: var(--secondary-text-color);
    border-color: var(--secondary-text-color);
    margin-right: 10px;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        color: var(--tertiary-text-color);
        background: var(--ghost-button-hover-color);
        border-color: var(--ghost-button-hover-color);
      }
    }

    &:active {
      background-color: var(--ghost-button-active-color);
      border-color: var(--ghost-button-active-color);
      color: var(--tertiary-text-color); /* Sorgt für gute Lesbarkeit (Weiß auf Grau) */
    }
  }

  & .button_ghost.is-hidden {
    visibility: hidden; /* Der Platzhalter bleibt erhalten, der Button wird unsichtbar */
    pointer-events: none; /* Verhindert Klicks auf den unsichtbaren Button */
  }

  & #main_content {
    margin-top: 0;
    &:focus,
    &:focus-visible {
      outline: none;
    }
  }
}
</style>

<script>
import { useWorkflowStore } from './stores/workflow.js';
import { onMounted, ref, watch, nextTick, computed } from 'vue';
import StateZipCodeQuestion from './components/questionStates/StateZipCodeQuestion.vue'
import StateAbortZipCode from "@/components/abortStates/StateAbortZipCode.vue";
import StateBuildingTypeQuestion from "@/components/questionStates/StateBuildingTypeQuestion.vue";
import StateAbortBuildingType from "@/components/abortStates/StateAbortBuildingType.vue";
import StateOwnerQuestion from "@/components/questionStates/StateOwnerQuestion.vue";
import StateAbortOwner from "@/components/abortStates/StateAbortOwner.vue";
import StateAlreadyBuiltQuestion from "@/components/questionStates/StateAlreadyBuiltQuestion.vue";
import StateAbortBuildingAge from "@/components/abortStates/StateAbortBuildingAge.vue";
import StatePlannedReplacementQuestion from "@/components/questionStates/StatePlannedReplacementQuestion.vue";
import StatePowerConsumersQuestion from "@/components/questionStates/StatePowerConsumersQuestion.vue";
import StateWishPowerStorageQuestion from "@/components/questionStates/StateWishPowerStorageQuestion.vue";
import StateContactForm from "@/components/contactformStates/StateContactForm.vue";
import StateContactFormSuccess from "@/components/contactformStates/StateContactFormSuccess.vue";
import ProgressBar from '@/components/ProgressBar.vue';
import StateWishWallboxQuestion from "@/components/questionStates/StateWishWallboxQuestion.vue";
import StateWishHeatPumpQuestion from "@/components/questionStates/StateWishHeatPumpQuestion.vue";
import StateAppointmentTypeQuestion from "@/components/questionStates/StateAppointmentTypeQuestion.vue";

export default {
  name: 'App',
  props: {
    crossdomainid: String,
    leveto_source: String,
    leveto_medium: String
  },
  components: {
    StateAppointmentTypeQuestion,
    StateWishHeatPumpQuestion,
    StateWishWallboxQuestion,
    StateWishPowerStorageQuestion,
    StatePowerConsumersQuestion,
    StateContactForm,
    StatePlannedReplacementQuestion,
    StateAbortBuildingAge,
    StateAlreadyBuiltQuestion,
    StateAbortOwner,
    StateOwnerQuestion,
    StateAbortBuildingType,
    StateZipCodeQuestion,
    StateAbortZipCode,
    StateBuildingTypeQuestion,
    StateContactFormSuccess,
    ProgressBar,
  },
  setup(props) {
    const workflowStore = useWorkflowStore();
    const mainContentRef = ref(null);
    const appWrapperRef = ref(null);

    const stateComponentMap = {
      zipCodeQuestion: StateZipCodeQuestion,
      zipCodeNotAvailable: StateAbortZipCode,
      powerConsumersQuestion: StatePowerConsumersQuestion,
      wishPowerStorageQuestion: StateWishPowerStorageQuestion,
      wishWallboxQuestion: StateWishWallboxQuestion,
      wishHeatpumpQuestion: StateWishHeatPumpQuestion,
      buildingTypeQuestion: StateBuildingTypeQuestion,
      buildingTypeNotAvailable: StateAbortBuildingType,
      ownerQuestion: StateOwnerQuestion,
      ownerNotAvailable: StateAbortOwner,
      alreadyBuiltQuestion: StateAlreadyBuiltQuestion,
      buildingYearNotAvailable: StateAbortBuildingAge,
      plannedReplacementQuestion: StatePlannedReplacementQuestion,
      appointmentTypeQuestion: StateAppointmentTypeQuestion,
      contactInformationQuestion: StateContactForm,
      sendingVlinkData: StateContactForm,
      submissionSuccess: StateContactFormSuccess,
    };

    // Computed Property liefert die aktuell aktive Komponente
    const currentComponent = computed(() => {
      return stateComponentMap[workflowStore.currentMachineState] || null;
    });
    const resizeIframeManually = () => {
      if (window.parentIFrame && typeof window.parentIFrame.size === 'function') {
        // 1. Warten, bis Vue die Daten im DOM verarbeitet hat
        nextTick(() => {
          setTimeout(() => {
            if (appWrapperRef.value) {
              const realHeight = appWrapperRef.value.getBoundingClientRect().height;
              const exactHeight = Math.ceil(realHeight);

              // Punktgenaue Höhe an die Host-Seite übermitteln
              window.parentIFrame.size(exactHeight);
            }
          }, 150);
        });
      }
    };
    onMounted(() => {
      workflowStore.initializeWorkflow();
      const urlParams = new URLSearchParams(window.location.search);
      //const defaultVertriebskanal = process.env.VUE_APP_VERTRIEBSKANAL;
      //const urlVertriebskanal = urlParams.get('vtkanal');
      //const finalVertriebskanal = urlVertriebskanal || defaultVertriebskanal;
      const finalVertriebskanal = process.env.VUE_APP_VERTRIEBSKANAL;
      if (finalVertriebskanal) {
        workflowStore.setVertriebskanal(finalVertriebskanal);
      }

      const finalSource = props.leveto_source || process.env.VUE_APP_DEFAULT_LEVETO_SOURCE || '';
      const finalMedium = props.leveto_medium || process.env.VUE_APP_DEFAULT_LEVETO_MEDIUM || '';


      if (finalSource) {
        workflowStore.setLevetoSource(finalSource);
      }
      if (finalMedium) {
        workflowStore.setLevetoMedium(finalMedium);
      }

      let finalCrossdomainId = props.crossdomainid || urlParams.get('crossdomainid') || '';


        const utmSource = urlParams.get('utm_source');
        const utmMedium = urlParams.get('utm_medium');
        const utmCampaign = urlParams.get('utm_campaign');

        // Nur wenn ALLE drei UTM-Parameter existieren, bauen wir den String
        if (utmSource && utmMedium && utmCampaign) {
          const utmString = `utm_source=${utmSource}~utm_medium=${utmMedium}~utm_campaign=${utmCampaign}`;
          finalCrossdomainId = finalCrossdomainId
              ? `${finalCrossdomainId}~${utmString}`
              : utmString;
        }


      if (finalCrossdomainId) {
        workflowStore.setCrossdomainId(encodeURIComponent(finalCrossdomainId));
      }
      resizeIframeManually();
    });
    watch(() => workflowStore.currentMachineState, (newState, oldState) => {
      // Sicherstellen dass ein Wechsel stattgefunden hat und das Ref existiert
      if (newState !== oldState && mainContentRef.value) {
        resizeIframeManually();
        // nextTick stellt sicher, dass das DOM aktualisiert wurde,
        // bevor der Fokus gesetzt wird
        nextTick(() => {
          mainContentRef.value.focus({ preventScroll: true });
          //window.scrollTo({ top: 110, behavior: 'instant' })
          //window.parent.postMessage('scrolling', '*');

          const rect = mainContentRef.value.getBoundingClientRect();
          const elementAbsoluteTop = rect.top + window.scrollY;
          const targetPosition = elementAbsoluteTop - 150;

          window.scrollTo({
            top: targetPosition,
            behavior: 'instant'
          });
          /*
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'clickstream',
            action: 'scrolling',
          });
          */
          if (process.env.NODE_ENV !== 'production') {
            console.log("Scrolling");
          }
        });
      }
    });
    return {
      workflowStore,
      mainContentRef,
      appWrapperRef,
      currentComponent,
    };
  }
}
</script>


